/**
 * Local Comment Storage
 * 
 * This module provides a fallback mechanism for storing comments
 * locally when Firebase is not working or available.
 */

// Local storage key prefix
const LOCAL_COMMENTS_KEY = 'flyp_comments_';
const LOCAL_REPLIES_KEY = 'flyp_replies_';

// Initialize local storage with empty arrays if needed
const initStorage = () => {
  // This is just a safety check
  if (typeof localStorage === 'undefined') {
    console.error('localStorage is not available');
    return false;
  }
  return true;
};

// Save a comment to local storage
export const saveCommentLocally = (comment) => {
  if (!initStorage()) return false;
  if (!comment || !comment.questionId) return false;
  
  // Get existing comments for this question
  const storageKey = `${LOCAL_COMMENTS_KEY}${comment.questionId}`;
  let existingComments = [];
  try {
    const storedComments = localStorage.getItem(storageKey);
    if (storedComments) {
      existingComments = JSON.parse(storedComments);
    }
  } catch (e) {
    console.error('Error reading comments from localStorage:', e);
  }
  
  // Add timestamp and ID if missing
  const finalComment = {
    ...comment,
    id: comment.id || `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    isLocalOnly: true,
    localCreatedAt: comment.localCreatedAt || new Date().toISOString()
  };
  
  // Add the new comment at the beginning
  existingComments.unshift(finalComment);
  
  // Save back to localStorage
  try {
    localStorage.setItem(storageKey, JSON.stringify(existingComments));
    console.log('Comment saved locally:', finalComment);
    return finalComment;
  } catch (e) {
    console.error('Error saving comment to localStorage:', e);
    return false;
  }
};

// Save a reply to local storage
export const saveReplyLocally = (reply) => {
  if (!initStorage()) return false;
  if (!reply || !reply.parentId) return false;
  
  // Get existing replies for this parent comment
  const storageKey = `${LOCAL_REPLIES_KEY}${reply.parentId}`;
  let existingReplies = [];
  try {
    const storedReplies = localStorage.getItem(storageKey);
    if (storedReplies) {
      existingReplies = JSON.parse(storedReplies);
    }
  } catch (e) {
    console.error('Error reading replies from localStorage:', e);
  }
  
  // Add timestamp and ID if missing
  const finalReply = {
    ...reply,
    id: reply.id || `local_reply_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    isLocalOnly: true,
    localCreatedAt: reply.localCreatedAt || new Date().toISOString()
  };
  
  // Add the new reply at the end
  existingReplies.push(finalReply);
  
  // Save back to localStorage
  try {
    localStorage.setItem(storageKey, JSON.stringify(existingReplies));
    console.log('Reply saved locally:', finalReply);
    return finalReply;
  } catch (e) {
    console.error('Error saving reply to localStorage:', e);
    return false;
  }
};

// Get comments for a question from local storage
export const getLocalComments = (questionId) => {
  if (!initStorage() || !questionId) return [];
  
  const storageKey = `${LOCAL_COMMENTS_KEY}${questionId}`;
  try {
    const storedComments = localStorage.getItem(storageKey);
    if (storedComments) {
      return JSON.parse(storedComments);
    }
  } catch (e) {
    console.error('Error reading comments from localStorage:', e);
  }
  
  return [];
};

// Get replies for a comment from local storage
export const getLocalReplies = (commentId) => {
  if (!initStorage() || !commentId) return [];
  
  const storageKey = `${LOCAL_REPLIES_KEY}${commentId}`;
  try {
    const storedReplies = localStorage.getItem(storageKey);
    if (storedReplies) {
      return JSON.parse(storedReplies);
    }
  } catch (e) {
    console.error('Error reading replies from localStorage:', e);
  }
  
  return [];
};

// Delete a comment from local storage
export const deleteLocalComment = (commentId, questionId, userId, isAdmin = false) => {
  if (!initStorage() || !commentId || !questionId) return false;
  
  const storageKey = `${LOCAL_COMMENTS_KEY}${questionId}`;
  try {
    const storedComments = localStorage.getItem(storageKey);
    if (storedComments) {
      const comments = JSON.parse(storedComments);
      
      // Find the comment to check permissions
      const commentToDelete = comments.find(comment => comment.id === commentId);
      
      // Check if comment exists and user has permission to delete
      if (!commentToDelete) {
        console.warn('Comment not found in local storage');
        return false;
      }
      
      // Check permissions - only allow delete if user is admin or comment owner
      if (!isAdmin && commentToDelete.userId !== userId) {
        console.error('Permission denied: User is not the owner or admin');
        return false;
      }
      
      // User has permission, remove the comment
      const filteredComments = comments.filter(comment => comment.id !== commentId);
      localStorage.setItem(storageKey, JSON.stringify(filteredComments));
    }
    return true;
  } catch (e) {
    console.error('Error deleting comment from localStorage:', e);
    return false;
  }
};

// Delete a reply from local storage
export const deleteLocalReply = (replyId, parentId, userId, isAdmin = false) => {
  if (!initStorage() || !replyId || !parentId) return false;
  
  const storageKey = `${LOCAL_REPLIES_KEY}${parentId}`;
  try {
    const storedReplies = localStorage.getItem(storageKey);
    if (storedReplies) {
      const replies = JSON.parse(storedReplies);
      
      // Find the reply to check permissions
      const replyToDelete = replies.find(reply => reply.id === replyId);
      
      // Check if reply exists and user has permission to delete it
      if (!replyToDelete) {
        console.warn('Reply not found in local storage');
        return false;
      }
      
      // Check permissions - only allow delete if user is admin or reply owner
      if (!isAdmin && replyToDelete.userId !== userId) {
        console.error('Permission denied: User is not the owner or admin');
        return false;
      }
      
      // User has permission, remove the reply
      const filteredReplies = replies.filter(reply => reply.id !== replyId);
      localStorage.setItem(storageKey, JSON.stringify(filteredReplies));
    }
    return true;
  } catch (e) {
    console.error('Error deleting reply from localStorage:', e);
    return false;
  }
};

// Clear all local comments for testing
export const clearAllLocalComments = () => {
  if (!initStorage()) return false;
  
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(LOCAL_COMMENTS_KEY) || key.startsWith(LOCAL_REPLIES_KEY)) {
        localStorage.removeItem(key);
      }
    });
    return true;
  } catch (e) {
    console.error('Error clearing local comments:', e);
    return false;
  }
};